import {
  CircularProgress,
  Divider,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { CustomButton } from "../../ui/CustomButton";
import { useState } from "react";
import { StarsOutlineIcon } from "../../images";
import { ReuseInput } from "../../ui";
import { ButtonsColorType } from "../../../types/enums";

type Props = {
  title: string;
  subtitle: string;
  submitHandler: (text: string) => Promise<void>;
  isLoading: boolean;
  placeholder: string;
};

export const AiAssistantBlock = ({
  title,
  subtitle,
  submitHandler,
  isLoading,
  placeholder,
}: Props) => {
  const { palette } = useTheme();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [text, setText] = useState<string>("");

  const openHandler = () => setIsOpen(true);
  const closeHandler = () => {
    setIsOpen(false);
    setText("");
  };

  const inputChange = (_: string, value: string) => {
    setText(value);
  };

  const onSubmit = async () => {
    await submitHandler(text);
    setIsOpen(false);
    setText("");
  };

  const isAnalyzeEnabled = !!text && !isLoading;

  return (
    <>
      <Stack
        flexDirection="row"
        gap="24px"
        justifyContent="space-between"
        alignItems={isOpen ? "flex-start" : "center"}
        sx={{
          padding: "16px",
          borderRadius: "6px",
          border: "1px solid",
          borderColor: "custom.accentBorder",
          bgcolor: "custom.accentSurface",
        }}
      >
        <Stack gap="16px" flex={1}>
          <Stack gap="8px">
            <Stack direction="row" alignItems="center" gap="8px">
              <Stack
                sx={{
                  padding: "4px",
                  borderRadius: "4px",
                  bgcolor: "custom.accentIconBg",
                  flexShrink: 0,
                }}
              >
                <StarsOutlineIcon color={palette.primary.dark} />
              </Stack>
              <Typography
                variant="subtitle1"
                color="custom.textDarkBlue"
                fontWeight={500}
              >
                {title}
              </Typography>
            </Stack>
            <Typography variant="body1" color="text.secondary">
              {subtitle}
            </Typography>
          </Stack>
          {isOpen && (
            <>
              <ReuseInput
                value={text}
                errorMessage={""}
                label={""}
                name={"AI assistant"}
                placeholder={placeholder}
                handleInputChange={inputChange}
                isError={false}
                setErrorHandler={() => {}}
                multiline
                maxRows={3}
                withResize={false}
                minHeight="80px"
                sx={{
                  "& .MuiInputBase-root": {
                    background: palette.background.default,
                    borderColor: palette.custom.textDisabled,
                  },
                }}
              />
              <Stack direction="row" gap="8px">
                <CustomButton
                  textColor={palette.text.secondary}
                  type="button"
                  customColor={palette.background.paper}
                  onClick={closeHandler}
                  fullWidth
                  sx={{ height: "36px" }}
                  disabled={isLoading}
                >
                  Cancel
                </CustomButton>
                <CustomButton
                  customColor={
                    isAnalyzeEnabled
                      ? ButtonsColorType.dark_orange
                      : palette.background.paper
                  }
                  textColor={
                    isAnalyzeEnabled ? "#FFFFFF" : palette.custom.textDisabled
                  }
                  type="button"
                  fullWidth
                  sx={{
                    height: "36px",
                    border: "none",
                    "&:hover": {
                      background: isAnalyzeEnabled
                        ? ButtonsColorType.dark_orange
                        : palette.background.paper,
                    },
                    "&.Mui-disabled": {
                      background: palette.background.paper,
                      color: palette.custom.textDisabled,
                    },
                  }}
                  onClick={onSubmit}
                  disabled={!isAnalyzeEnabled}
                  startIcon={
                    isLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <StarsOutlineIcon
                        color={
                          isAnalyzeEnabled
                            ? "#FFFFFF"
                            : palette.custom.textDisabled
                        }
                        filled
                      />
                    )
                  }
                >
                  Analyze And Fill
                </CustomButton>
              </Stack>
            </>
          )}
        </Stack>
        {!isOpen && (
          <CustomButton
            customColor={ButtonsColorType.dark_orange}
            textColor="#FFFFFF"
            customSize="fit"
            type="button"
            sx={{
              height: "40px",
              padding: "8px 16px",
              borderRadius: "8px",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
            startIcon={<StarsOutlineIcon color="#FFFFFF" filled />}
            onClick={openHandler}
          >
            Quick fill with AI
          </CustomButton>
        )}
      </Stack>
    </>
  );
};
